import { accountEndpoints } from 'store/accounts/services';
import { authEndpoints } from 'store/auth/services';
import { commonEndpoints } from 'store/common/services';
import { marketplaceEndpoints } from 'store/marketplace/services';
import { orderEndpoints } from 'store/orders/services';
import { organizationEndpoints } from 'store/organizations/services';
import { proxyEndpoints } from 'store/proxies/services';
import { referralsEndpoints } from 'store/referrals/services';

import { base } from './base';

export const api = base.injectEndpoints({
  endpoints: (builder) => ({
    ...accountEndpoints(builder),
    ...authEndpoints(builder),
    ...commonEndpoints(builder),
    ...marketplaceEndpoints(builder),
    ...orderEndpoints(builder),
    ...organizationEndpoints(builder),
    ...proxyEndpoints(builder),
    ...referralsEndpoints(builder),
  }),
});

export const {
  // #region Account
  /** Queries */
  useGetAccountBalanceQuery,
  useGetAccountProfileQuery,
  useGetApiKeysQuery,
  useGetBillingPreferencesQuery,
  useGetCreditCardQuery,
  useGetIdentityVerificationTokenQuery,
  useGetInvoicesQuery,
  useGetNotificationPreferencesQuery,
  useGetTopUpQuery,
  useGetTransactionsQuery,
  useGetTwoFactorAuthenticationMethodsQuery,
  useGetWebhookQuery,
  useGetWebhooksQuery,
  useGetWebhookDeliveriesQuery,
  useGetWebhookDeliveryQuery,
  useGetWebPushNotificationKeyQuery,

  /** Lazy Queries */
  useLazyGetAccountBalanceQuery,
  useLazyGetAccountProfileQuery,
  useLazyGetApiKeysQuery,
  useLazyGetBillingPreferencesQuery,
  useLazyGetCreditCardQuery,
  useLazyGetIdentityVerificationTokenQuery,
  useLazyGetInvoicesQuery,
  useLazyGetNotificationPreferencesQuery,
  useLazyGetTopUpQuery,
  useLazyGetTransactionsQuery,
  useLazyGetTwoFactorAuthenticationMethodsQuery,
  useLazyGetWebhookQuery,
  useLazyGetWebhooksQuery,
  useLazyGetWebhookDeliveriesQuery,
  useLazyGetWebhookDeliveryQuery,
  useLazyGetWebPushNotificationKeyQuery,

  /** Mutations */
  useAddCreditCardMutation,
  useBulkDownloadInvoicesMutation,
  useChangeLanguageMutation,
  useChangePasswordMutation,
  useCreateWebhookMutation,
  useDeleteApiKeyMutation,
  useDeleteCreditCardMutation,
  useDeleteWebhookMutation,
  useDisableHardwareTwoFactorMutation,
  useDisableSoftwareTwoFactorMutation,
  useEditWebhookMutation,
  useEnableHardwareTwoFactorMutation,
  useEnableSoftwareTwoFactorMutation,
  useGenerateApiKeyMutation,
  useGetTopUpFeesMutation,
  useResendEmailVerificationLinkMutation,
  useRetryWebhookMutation,
  useSkipProfileDetailsMutation,
  useSubscribeWebPushNotificationsMutation,
  useTopUpMutation,
  useUnsubscribeWebPushNotificationsMutation,
  useUpdateAccountProfileMutation,
  useUpdateBillingPreferencesMutation,
  useUpdateNotificationPreferencesMutation,
  useTestWebhookMutation,
  // #endregion Account

  // #region Auth
  /** Queries */
  useGetCaptchaQuery,
  useGetMultiFactorStatusQuery,
  useGetSessionQuery,

  /** Lazy Queries */
  useLazyGetCaptchaQuery,
  useLazyGetMultiFactorStatusQuery,
  useLazyGetSessionQuery,

  /** Mutations */
  useExchangeSessionTokenMutation,
  useForgotPasswordMutation,
  useLoginMutation,
  useLogoutMutation,
  useMultiFactorVerificationMutation,
  useRegisterMutation,
  useResetPasswordMutation,
  useSwitchProductMutation,
  // #endregion Auth

  // #region Common
  /** Queries */
  useGetExperimentsQuery,
  useGetFeaturesQuery,
  useGetMaintenanceWindowsQuery,

  /** Lazy Queries */
  useLazyGetExperimentsQuery,
  useLazyGetFeaturesQuery,
  useLazyGetMaintenanceWindowsQuery,
  // #endregion Common

  // #region Organizations
  /** Queries */
  useGetOrganizationCreditCardQuery,
  useGetOrganizationsQuery,
  useGetOrganizationDetailsQuery,
  useGetOrganizationMembersQuery,

  /** Lazy Queries */
  useLazyGetOrganizationCreditCardQuery,
  useLazyGetOrganizationsQuery,
  useLazyGetOrganizationDetailsQuery,
  useLazyGetOrganizationMembersQuery,

  /** Mutations */
  useAddOrganizationCreditCardMutation,
  useDeleteOrganizationCreditCardMutation,
  useUpdateOrganizationMutation,
  useCreateOrganizationMutation,
  useAddOrganizationMemberMutation,
  useUpdateOrganizationMemberRoleMutation,
  useDeleteOrganizationMemberMutation,
  useSetActiveOrganizationMutation,
  // #endregion Organizations

  // #region Order
  /** Queries */
  useGetOrderQuery,
  useGetOrderServicesQuery,
  useGetOrderServiceDetailsQuery,

  /** Lazy Queries */
  useLazyGetOrderQuery,
  useLazyGetOrderServicesQuery,
  useLazyGetOrderServiceDetailsQuery,

  /** Mutations */
  useGetOrderServicePriceMutation,
  useExecuteServiceOrderMutation,
  // #endregion Order

  // #region Proxy
  /** Queries */
  useGetBulkAutoExtendSettingsQuery,
  useGetBulkProxyExtendBandwidthPriceQuery,
  useGetBulkProxyExtendPeriodPriceQuery,
  useGetBulkOperationQuery,
  useGetBulkOperationTasksQuery,
  useGetBulkProxyReactivatePriceQuery,
  useGetChangeAuthenticationTypeOptionsQuery,
  useGetChangeProtocolOptionsQuery,
  useGetChangeThreadsOptionsQuery,
  useGetChangeUplinkSpeedOptionsQuery,
  useGetGlobalIPWhitelistQuery,
  useGetCredentialsGeneratorQuery,
  useGetIsProxyReplacementAvailableQuery,
  useGetProxyQuery,
  useGetProxyAdminDetailsQuery,
  useGetProxyChangelogQuery,
  useGetProxyEventsQuery,
  useGetProxyExtendBandwidthPriceQuery,
  useGetProxyExtendPeriodPriceQuery,
  useGetProxyIPWhitelistQuery,
  useGetProxyLimitSignalsQuery,
  useGetProxySubnetsQuery,
  useGetProxiesQuery,
  useGetProxiesISPsQuery,
  useGetProxiesSummaryQuery,
  useGetReactivateProxyPriceQuery,
  useGetReplaceableProxiesQuery,
  useGetUpgradeThreadsPriceQuery,
  useGetUpgradeUplinkSpeedPriceQuery,

  /** Lazy Queries */
  useLazyGetBulkAutoExtendSettingsQuery,
  useLazyGetBulkProxyExtendBandwidthPriceQuery,
  useLazyGetBulkProxyExtendPeriodPriceQuery,
  useLazyGetBulkOperationQuery,
  useLazyGetChangeAuthenticationTypeOptionsQuery,
  useLazyGetChangeProtocolOptionsQuery,
  useLazyGetChangeThreadsOptionsQuery,
  useLazyGetChangeUplinkSpeedOptionsQuery,
  useLazyGetGlobalIPWhitelistQuery,
  useLazyGetBulkProxyReactivatePriceQuery,
  useLazyGetCredentialsGeneratorQuery,
  useLazyGetIsProxyReplacementAvailableQuery,
  useLazyGetProxyQuery,
  useLazyGetProxyAdminDetailsQuery,
  useLazyGetProxyChangelogQuery,
  useLazyGetProxyEventsQuery,
  useLazyGetProxyExtendBandwidthPriceQuery,
  useLazyGetProxyExtendPeriodPriceQuery,
  useLazyGetProxyIPWhitelistQuery,
  useLazyGetProxyLimitSignalsQuery,
  useLazyGetProxySubnetsQuery,
  useLazyGetProxiesQuery,
  useLazyGetProxiesISPsQuery,
  useLazyGetProxiesSummaryQuery,
  useLazyGetReactivateProxyPriceQuery,
  useLazyGetReplaceableProxiesQuery,
  useLazyGetUpgradeThreadsPriceQuery,
  useLazyGetUpgradeUplinkSpeedPriceQuery,

  /** Mutations */
  useActivateProxyMutation,
  useAddProxyRouteMutation,
  useBulkAutoExtendProxyMutation,
  useBulkChangeProxyAuthenticationTypeMutation,
  useBulkChangeProtocolMutation,
  useBulkDownloadProxyCredentialsMutation,
  useBulkExtendProxyBandwidthMutation,
  useBulkExtendProxyPeriodMutation,
  useBulkReactivateProxyMutation,
  useCancelProxyMutation,
  useChangeProxyPortMutation,
  useChangeThreadsMutation,
  useChangeUplinkSpeedMutation,
  useDeleteProxyRouteMutation,
  useDisableProxyAutoExtendMutation,
  useDisableProxyHttpsMutation,
  useEnableProxyAutoExtendMutation,
  useEnableProxyHttpsMutation,
  useExpireProxyMutation,
  useExtendProxyBandwidthMutation,
  useExtendProxyPeriodMutation,
  useGetProxyReplacementOptionsMutation,
  useInitializeProxyMutation,
  useReactivateProxyMutation,
  useRefundProxyMutation,
  useRegenerateCredentialsMutation,
  useReplaceProxyMutation,
  useRequestCountryMutation,
  useReviveProxyMutation,
  useRotateIpMutation,
  useTestProxyMutation,
  useRunDiagnosticRoutineMutation,
  useUpdateAuthenticationTypeMutation,
  useUpdateGlobalIPWhitelistMutation,
  useUpdateIPWhitelistMutation,
  useUpdateProxyProtocolMutation,
  useUpgradeThreadsMutation,
  useUpgradeUplinkSpeedMutation,
  useUpsertProxyNoteMutation,
  // #endregion

  // #region Referrals
  /** Queries */
  useGetBankDetailsQuery,
  useGetPayoutDetailsQuery,
  useGetReferralsDetailsQuery,
  useGetReferralEarningsSummaryQuery,
  useGetReferralPayoutRequestsQuery,
  useGetReferralTransactionsQuery,
  useGetReferredUsersQuery,

  /** Lazy Queries */
  useLazyGetBankDetailsQuery,
  useLazyGetPayoutDetailsQuery,
  useLazyGetReferralsDetailsQuery,
  useLazyGetReferralEarningsSummaryQuery,
  useLazyGetReferralPayoutRequestsQuery,
  useLazyGetReferralTransactionsQuery,
  useLazyGetReferredUsersQuery,

  /** Mutations */
  useReferralsPayoutMutation,
  useUpsertBankDetailsMutation,
  // #endregion Referrals

  // @deprecated
  // Marketplace
  useGetMarketplaceDealsQuery,
  useGetMarketplaceAvailableCountriesQuery,

  // Order
} = api;
